import { useState } from 'react';
import Image from 'next/image';

import { Box, Button, Grid, Heading } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ClampedTextV2 } from '~/components/clamped-text-v2';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { useI18n } from '~/hooks/use-i18n';

import { ItemCard } from './item-card';

export type ProgramListSectionProps = {
  title?: string | null;
  programs: Array<Pick<CatalogItem, 'key' | 'image' | 'summary' | 'slug' | 'programDescriptionOverride' | 'title'>>;
};

export function ProgramListSection({ title, programs }: ProgramListSectionProps) {
  return (
    <Box
      as='section'
      bgColor='white'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer>
        {!!title && (
          <HeadingWithEmphasis
            text={title}
            emphasisProps={{
              color: 'blue.400',
              fontWeight: '500',
            }}
            headingProps={{
              as: 'h2',
              textAlign: 'center',
              mb: {
                base: 8,
                md: '48px',
              },
              size: {
                base: 'marketing-3xl',
                md: 'marketing-4xl',
              },
            }}
          />
        )}
        <Grid
          gap={{
            base: 8,
            md: '48px',
          }}
          gridTemplateColumns={{
            base: '1fr',
            md: 'repeat(auto-fill, minmax(341px, 1fr))',
          }}
        >
          {programs.map((program) => (
            <ProgramCard key={program.key} program={program} />
          ))}
        </Grid>
      </ContentContainer>
    </Box>
  );
}

function ProgramCard({
  program,
}: {
  program: Pick<CatalogItem, 'key' | 'image' | 'summary' | 'slug' | 'programDescriptionOverride' | 'title'>;
}) {
  const courseLink = useCourseLink({ courseSlug: program.slug });
  const [expandCopy, setExpandCopy] = useState(false);
  const { t } = useI18n();
  return (
    <ItemCard
      buttonSlot={
        <ButtonLink
          buttonProps={{
            variant: 'outline',
            width: 'fit-content',
            onClick: () => {
              // TODO: fire track event
            },
          }}
          linkProps={{
            href: courseLink,
          }}
        >
          View Course
        </ButtonLink>
      }
      imageSlot={
        !!program.image && (
          <Image
            alt=''
            sizes='341px'
            src={program.image}
            style={{
              objectFit: 'cover',
            }}
            fill
          />
        )
      }
      summary={
        <Box
          flexBasis={{
            md: '104px',
          }}
        >
          <ClampedTextV2
            noOfLines={3}
            renderButton={({ isClamped, onToggle }) => {
              return (
                <Button
                  marginTop={2}
                  paddingStart={0}
                  variant='link'
                  onClick={() => {
                    setExpandCopy(isClamped);
                    onToggle();
                  }}
                >
                  {isClamped ? t('common.seeMore') : t('common.seeLess')}
                </Button>
              );
            }}
          >
            {program.programDescriptionOverride ?? program.summary}
          </ClampedTextV2>
        </Box>
      }
      title={
        <Box
          flexBasis={{
            md: '72px',
          }}
        >
          <Heading
            noOfLines={expandCopy ? undefined : 2}
            size={{
              base: 'marketing-xl',
              md: 'marketing-2xl',
            }}
          >
            {program.title}
          </Heading>
        </Box>
      }
    />
  );
}
